import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDropzone } from 'react-dropzone'
import { uploadFiles } from '../api/client'
import { useStore } from '../store'

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const EMAIL_EXTENSIONS = ['.eml', '.msg']

function isEmailFile(name: string): boolean {
  return EMAIL_EXTENSIONS.some(ext => name.toLowerCase().endsWith(ext))
}

export function UploadZone() {
  const { t } = useTranslation()
  const [files, setFiles] = useState<File[]>([])
  const { setCurrentSession, setLoading, setError, isLoading, setActiveView } = useStore()

  const onDrop = useCallback((accepted: File[]) => {
    const hasEmail = accepted.some(f => isEmailFile(f.name))
    if (hasEmail) {
      const emailFile = accepted.find(f => isEmailFile(f.name))!
      setFiles([emailFile])
      return
    }
    setFiles((prev) => {
      const prevClean = prev.filter(f => !isEmailFile(f.name))
      const combined = [...prevClean, ...accepted]
      const unique = Array.from(new Map(combined.map((f) => [f.name, f])).values())
      return unique.slice(0, 3)
    })
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/xml': ['.xml'],
      'application/xml': ['.xml'],
      'message/rfc822': ['.eml'],
      'application/vnd.ms-outlook': ['.msg'],
    },
    maxFiles: 3,
  })

  const hasEmail = files.some(f => isEmailFile(f.name))

  const removeFile = (name: string) => setFiles((prev) => prev.filter((f) => f.name !== name))

  const handleSubmit = async () => {
    if (files.length < 1) {
      setError(t('upload_zone.error_min_files'))
      return
    }
    setLoading(true)
    setError(null)
    try {
      const session = await uploadFiles(files)
      setCurrentSession(session)
      setActiveView('result')
      setFiles([])
    } catch (e) {
      setError(e instanceof Error ? e.message : t('upload_zone.error_unknown'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors ${
          isDragActive
            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
            : 'border-gray-300 dark:border-gray-600 hover:border-blue-400 hover:bg-gray-50 dark:hover:bg-gray-800'
        }`}
      >
        <input {...getInputProps()} />
        <div className="text-4xl mb-3">📂</div>
        <p className="text-gray-700 dark:text-gray-300 font-medium">
          {isDragActive ? t('upload_zone.drag_release') : t('upload_zone.drag_hint')}
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          {t('upload_zone.file_hint')}
        </p>
      </div>

      {files.length > 0 && (
        <>
          <ul className="space-y-2">
            {files.map((f) => (
              <li
                key={f.name}
                className="flex items-center justify-between bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-2 text-sm"
              >
                <span className="text-gray-700 dark:text-gray-300 truncate">{f.name}</span>
                {isEmailFile(f.name) && (
                  <span className="ml-2 flex-shrink-0 text-xs font-medium text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800/50 rounded px-1.5 py-0.5">
                    {t('upload_zone.email_badge')}
                  </span>
                )}
                <span className="ml-3 text-gray-400 dark:text-gray-500 flex-shrink-0 text-xs">{formatFileSize(f.size)}</span>
                <button
                  onClick={() => removeFile(f.name)}
                  className="ml-3 text-gray-400 hover:text-red-500 flex-shrink-0"
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>
          {hasEmail && (
            <p className="text-xs text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800/50 rounded-lg px-3 py-2">
              {t('upload_zone.email_extract_hint')}
            </p>
          )}
        </>
      )}

      {isLoading && (
        <div className="space-y-1.5">
          <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div className="h-full bg-blue-500 rounded-full animate-progress-indeterminate" />
          </div>
          <p className="text-xs text-center text-gray-500 dark:text-gray-400">
            {hasEmail ? t('upload_zone.processing_email') : t('upload_zone.processing_files')}
          </p>
        </div>
      )}

      <button
        onClick={handleSubmit}
        disabled={files.length < 1 || isLoading}
        className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white font-medium py-2.5 rounded-lg text-sm transition-colors"
      >
        {isLoading
          ? t('upload_zone.btn_processing')
          : hasEmail
            ? t('upload_zone.btn_process_email')
            : files.length === 1
              ? t('upload_zone.btn_analyse')
              : t('upload_zone.btn_compare')}
      </button>
    </div>
  )
}
